import React from "react";
import {
  Box,
  Button,
  Chip,
  IconButton,
  Slider,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import PhotoLibraryOutlinedIcon from "@mui/icons-material/PhotoLibraryOutlined";
import {
  AssetKind,
  isColor,
  isHttpUrl,
} from "../../../../service/BuyerConfiguration/BuyerAppDashboard/homeThemesService";
import { GREEN, toHex6 } from "./themeUtils";
import { AssetLibrary } from "./AssetLibrary";

/** Colour picker + text field (hex or rgba). */
export const ColorField: React.FC<{
  label: string;
  value: string | null | undefined;
  onChange: (v: string) => void;
  hint?: string;
  /** Allows clearing (e.g. optional colours). */
  onClear?: () => void;
  placeholder?: string;
}> = ({ label, value, onChange, hint, onClear, placeholder }) => {
  const [text, setText] = React.useState(value ?? "");
  React.useEffect(() => setText(value ?? ""), [value]);
  const bad = !!text && !isColor(text);
  return (
    <Stack direction="row" spacing={1} alignItems="flex-start">
      <Box
        component="label"
        sx={{
          width: 38,
          height: 38,
          borderRadius: 1.5,
          border: "1px solid rgba(0,0,0,0.2)",
          flexShrink: 0,
          cursor: "pointer",
          position: "relative",
          overflow: "hidden",
          background:
            "repeating-conic-gradient(#ddd 0% 25%, #fff 0% 50%) 50% / 10px 10px",
        }}
      >
        <Box sx={{ position: "absolute", inset: 0, bgcolor: isColor(text) ? text : "transparent" }} />
        <input
          type="color"
          aria-label={label}
          value={toHex6(value, "#ffffff")}
          onChange={(e) => onChange(e.target.value.toUpperCase())}
          style={{ position: "absolute", inset: 0, opacity: 0, cursor: "pointer", width: "100%", height: "100%" }}
        />
      </Box>
      <TextField
        size="small"
        label={label}
        value={text}
        placeholder={placeholder ?? "#RRGGBB or rgba(…)"}
        error={bad}
        helperText={bad ? "Use #RRGGBB, #RGB or rgba(r,g,b,a)" : hint}
        onChange={(e) => {
          setText(e.target.value);
          if (isColor(e.target.value)) onChange(e.target.value.trim());
        }}
        onBlur={() => {
          if (!text && onClear) onClear();
          else if (!isColor(text)) setText(value ?? "");
        }}
        fullWidth
        InputProps={{
          endAdornment: onClear && value ? (
            <IconButton size="small" onClick={onClear} aria-label={`Clear ${label}`}>
              <CloseIcon fontSize="small" />
            </IconButton>
          ) : undefined,
        }}
      />
    </Stack>
  );
};

/** Labelled slider with its value. */
export const SliderRow: React.FC<{
  label: string;
  value: number;
  min: number;
  max: number;
  step: number;
  onChange: (v: number) => void;
  format?: (v: number) => string;
  hint?: string;
}> = ({ label, value, min, max, step, onChange, format, hint }) => (
  <Box>
    <Stack direction="row" justifyContent="space-between" alignItems="baseline">
      <Typography sx={{ fontSize: 13, fontWeight: 600 }}>{label}</Typography>
      <Typography sx={{ fontSize: 12, color: "text.secondary" }}>
        {format ? format(value) : value}
      </Typography>
    </Stack>
    <Slider
      size="small"
      value={value}
      min={min}
      max={max}
      step={step}
      onChange={(_, v) => onChange(v as number)}
      sx={{ color: GREEN, py: 1 }}
    />
    {hint && (
      <Typography sx={{ fontSize: 11.5, color: "text.secondary", mt: -0.5 }}>{hint}</Typography>
    )}
  </Box>
);

export const pct = (v: number) => `${Math.round(v * 100)}%`;
export const times = (v: number) => `${v.toFixed(2).replace(/\.?0+$/, "")}×`;

/** A list of short strings as chips with an add box. */
export const ChipsInput: React.FC<{
  label: string;
  values: string[];
  onChange: (v: string[]) => void;
  max: number;
  placeholder?: string;
  validate?: (v: string) => string | null;
  /** Show a colour dot in each chip. */
  colors?: boolean;
  maxLength?: number;
}> = ({ label, values, onChange, max, placeholder, validate, colors, maxLength }) => {
  const [text, setText] = React.useState("");
  const [err, setErr] = React.useState<string | null>(null);
  const add = () => {
    const v = text.trim();
    if (!v) return;
    const e = validate ? validate(v) : null;
    if (e) {
      setErr(e);
      return;
    }
    if (values.length >= max) {
      setErr(`Up to ${max}`);
      return;
    }
    onChange([...values, maxLength ? v.slice(0, maxLength) : v]);
    setText("");
    setErr(null);
  };
  return (
    <Box>
      <Typography sx={{ fontSize: 13, fontWeight: 600, mb: 0.75 }}>
        {label}{" "}
        <Typography component="span" sx={{ fontSize: 12, color: "text.secondary", fontWeight: 400 }}>
          ({values.length}/{max})
        </Typography>
      </Typography>
      <Stack direction="row" flexWrap="wrap" sx={{ gap: 0.75, mb: values.length ? 1 : 0 }}>
        {values.map((v, i) => (
          <Chip
            key={`${v}-${i}`}
            label={v}
            size="small"
            onDelete={() => onChange(values.filter((_, j) => j !== i))}
            avatar={
              colors ? (
                <Box sx={{ bgcolor: v, borderRadius: "50%", border: "1px solid rgba(0,0,0,0.2)" }} />
              ) : undefined
            }
          />
        ))}
      </Stack>
      <Stack direction="row" spacing={1} alignItems="flex-start">
        {colors && (
          <input
            type="color"
            aria-label={`Pick a colour for ${label}`}
            value={toHex6(isColor(text) ? text : null, "#ffffff")}
            onChange={(e) => setText(e.target.value.toUpperCase())}
            style={{ width: 38, height: 38, border: "none", padding: 0, background: "none", cursor: "pointer" }}
          />
        )}
        <TextField
          size="small"
          value={text}
          placeholder={placeholder}
          onChange={(e) => {
            setText(e.target.value);
            setErr(null);
          }}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              add();
            }
          }}
          error={!!err}
          helperText={err ?? undefined}
          disabled={values.length >= max}
          sx={{ flex: 1 }}
        />
        <Button
          variant="outlined"
          onClick={add}
          disabled={values.length >= max || !text.trim()}
          sx={{ textTransform: "none", borderColor: GREEN, color: GREEN, height: 40 }}
        >
          Add
        </Button>
      </Stack>
    </Box>
  );
};

/** URL text field + "Library" button that opens the asset library for a kind. */
export const AssetUrlField: React.FC<{
  label: string;
  kind: AssetKind;
  value: string | null;
  onChange: (v: string | null) => void;
  themeId?: number;
  hint?: string;
}> = ({ label, kind, value, onChange, themeId, hint }) => {
  const [open, setOpen] = React.useState(false);
  const bad = !!value && !isHttpUrl(value);
  return (
    <>
      <Stack direction="row" spacing={1} alignItems="flex-start">
        <TextField
          size="small"
          label={label}
          value={value ?? ""}
          onChange={(e) => onChange(e.target.value.trim() ? e.target.value : null)}
          placeholder="https://…"
          fullWidth
          error={bad}
          helperText={bad ? "Must be an http(s) link, or it is dropped on save" : hint}
          InputProps={{
            endAdornment: value ? (
              <Tooltip title="Clear">
                <IconButton size="small" onClick={() => onChange(null)} aria-label={`Clear ${label}`}>
                  <CloseIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            ) : undefined,
          }}
        />
        <Button
          variant="outlined"
          startIcon={<PhotoLibraryOutlinedIcon />}
          onClick={() => setOpen(true)}
          sx={{ textTransform: "none", borderColor: GREEN, color: GREEN, height: 40, whiteSpace: "nowrap", flexShrink: 0 }}
        >
          Library
        </Button>
      </Stack>
      {open && (
        <AssetLibrary
          open
          kind={kind}
          lockKind
          themeId={themeId}
          onClose={() => setOpen(false)}
          onSelect={(url) => {
            onChange(url);
            setOpen(false);
          }}
        />
      )}
    </>
  );
};
